import { styled } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import { brand } from "../theme";

// ── Sub-tab switcher ────────────────────────────────────────
export const SubTabs = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2, 0.5),
  background: brand.surface,
  position: "sticky",
  top: 0,
  zIndex: 5,
}));

export const SubTab = styled(({ active, className, ...p }) => (
  <button className={className} {...p} />
))(({ active }: { active?: boolean }) => ({
  flex: 1,
  height: 36,
  border: "none",
  cursor: "pointer",
  borderRadius: 10,
  background: active ? brand.amber : brand.white,
  color: active ? brand.amberDark : brand.textSec,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0.4,
  boxShadow: active
    ? `0 2px 10px ${brand.amber}40`
    : `inset 0 0 0 1px ${brand.sep}`,
  transition: "all .15s",
  fontFamily: "inherit",
}));

// ── Section headings ────────────────────────────────────────
export const SectionHeader = styled(({ label, hint, className }) => (
  <div className={className}>
    <span className="label">{label}</span>
    {hint && <span className="hint">{hint}</span>}
  </div>
))(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1.25),
  padding: "0 2px",

  "& .label": {
    fontSize: 11,
    fontWeight: 800,
    color: brand.textSec,
    letterSpacing: 0.9,
    textTransform: "uppercase",
  },
  "& .hint": {
    fontSize: 10,
    color: brand.textDis,
    fontStyle: "italic",
  },
}));

// ── Podium (top 3) ──────────────────────────────────────────
const PODIUM_COLORS = ["#F5A623", "#B8AA92", "#C97B3C"]; // gold, silver-warm, bronze
const PODIUM_HEIGHTS: { [k: number]: number } = { 1: 112, 2: 94, 3: 78 };
const PODIUM_EMOJI: { [k: number]: string } = { 1: "👑", 2: "🥈", 3: "🥉" };

export const PodiumWrap = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 2, 0),
}));

export const PodiumRow = styled("div")({
  display: "flex",
  gap: 8,
  alignItems: "flex-end",
});

export const PodiumPillar = styled(({ rank, name, points, className }) => {
  const color = PODIUM_COLORS[rank - 1];
  const isFirst = rank === 1;
  return (
    <div className={className}>
      <div className="emoji">{PODIUM_EMOJI[rank]}</div>
      <div className="name">{name}</div>
      <div
        className="pillar"
        style={{
          height: PODIUM_HEIGHTS[rank],
          background: `linear-gradient(180deg, ${color} 0%, ${color}CC 100%)`,
          color: isFirst ? brand.amberDark : brand.white,
          boxShadow: isFirst
            ? `0 6px 22px ${color}66`
            : `0 3px 10px ${color}40`,
          borderRadius: "14px 14px 6px 6px",
        }}
      >
        <div className="points">{points}</div>
        <div className="pts-label">PTS</div>
        <div className="rank">#{rank}</div>
      </div>
    </div>
  );
})({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  "& .emoji": { fontSize: 22 },
  "& .name": {
    fontSize: 12,
    fontWeight: 700,
    color: brand.text,
    textAlign: "center",
    lineHeight: 1.2,
    padding: "0 2px",
  },
  "& .pillar": {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .pillar .points": {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1,
  },
  "& .pillar .pts-label": {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.8,
    marginTop: 2,
    opacity: 0.85,
  },
  "& .pillar .rank": {
    fontSize: 18,
    fontWeight: 800,
    marginTop: 8,
    opacity: 0.55,
  },
});

// ── Full ranking list (positions 4+) ────────────────────────
export const RankingCard = styled("div")({
  background: brand.white,
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 1px 4px rgba(26,18,5,0.06)",
});

export const RankingItem = styled(
  ({ rank, name, points, isTied, tieBreakerReason, isLast, className }) => {
    const negative = (points ?? 0) < 0;
    return (
      <div className={`${className}${isLast ? " is-last" : ""}`}>
        <div
          className={`rank-badge${negative ? " neg" : ""}`}
          aria-hidden
        >
          {rank}
        </div>
        <div className="body">
          <div className="name">{name}</div>
          {isTied && tieBreakerReason && (
            <div className="tie">
              <span aria-hidden>🔸</span>
              <span>Tie: {tieBreakerReason}</span>
            </div>
          )}
          {isTied && !tieBreakerReason && (
            <div className="tie">
              <span aria-hidden>🔸</span>
              <span>Tie: drink-off</span>
            </div>
          )}
        </div>
        <div className="score">
          <div className={`points${negative ? " neg" : ""}`}>
            {`${(points ?? 0) > 0 ? "+" : ""}${points ?? 0} Points`}
          </div>
        </div>
      </div>
    );
  },
)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderBottom: `1px solid ${brand.sep}`,
  "&.is-last": { borderBottom: "none" },

  "& .rank-badge": {
    width: 28,
    height: 28,
    borderRadius: 8,
    flexShrink: 0,
    background: brand.surface,
    color: brand.textSec,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
  },
  "& .rank-badge.neg": {
    background: brand.errorBg,
    color: brand.error,
  },
  "& .body": {
    flex: 1,
    minWidth: 0,
  },
  "& .name": {
    fontSize: 14,
    fontWeight: 600,
    color: brand.text,
  },
  "& .tie": {
    fontSize: 11,
    color: brand.textSec,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  "& .tie span:first-child": {
    fontSize: 9,
  },
  "& .score": {
    textAlign: "right",
    flexShrink: 0,
  },
  "& .points": {
    fontSize: 13,
    fontWeight: 800,
    color: brand.text,
    lineHeight: 1,
  },
  "& .points.neg": {
    color: brand.error,
  },
});

// ── Best Taste list with bar viz ────────────────────────────
export const BestTasteCard = RankingCard;

export const BestTasteItem = styled(
  ({ rank, name, points, pct, isLast, className }) => (
    <div className={`${className}${isLast ? " is-last" : ""}`}>
      <div className="rank">{rank}</div>
      <div className="body">
        <div className="name">{name}</div>
        <div className="bar-track">
          <div
            className="bar-fill"
            style={{
              width: `${Math.max(4, pct * 100)}%`,
              background: rank <= 3 ? brand.amber : `${brand.textDis}AA`,
            }}
          />
        </div>
      </div>
      <div className="score">{`${points} Points`}</div>
    </div>
  ),
)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 14px",
  borderBottom: `1px solid ${brand.sep}`,
  "&.is-last": { borderBottom: "none" },

  "& .rank": {
    width: 22,
    fontSize: 12,
    fontWeight: 700,
    color: brand.textSec,
    textAlign: "center",
  },
  "& .body": {
    flex: 1,
    minWidth: 0,
  },
  "& .name": {
    fontSize: 14,
    fontWeight: 600,
    color: brand.text,
  },
  "& .bar-track": {
    height: 4,
    borderRadius: 2,
    background: brand.sep,
    marginTop: 5,
    overflow: "hidden",
  },
  "& .bar-fill": {
    height: "100%",
    borderRadius: 2,
  },
  "& .score": {
    fontSize: 13,
    fontWeight: 700,
    color: brand.text,
    flexShrink: 0,
    minWidth: 70,
    textAlign: "right",
  },
});

// ── Award cards (Beer Lover / Beer Hater) ───────────────────
export const AwardGrid = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.25, 2, 0),
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
}));

export const AwardCard = styled(({ tone, emoji, label, name, className }) => (
  <div className={`${className} ${tone}`}>
    <div className="head">
      <span className="emoji" aria-hidden>
        {emoji}
      </span>
      <span className="label">{label}</span>
    </div>
    <div className="receiver">{name}</div>
  </div>
))({
  borderRadius: 12,
  padding: "12px 12px 14px",
  border: "1px solid transparent",

  "&.pos": {
    background: brand.amberLight,
    borderColor: `${brand.amberDeep}30`,
  },
  "&.neg": {
    background: brand.errorBg,
    borderColor: `${brand.error}30`,
  },

  "& .head": {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  "& .emoji": { fontSize: 16 },
  "& .label": {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  "&.pos .label": { color: brand.amberDeep },
  "&.neg .label": { color: brand.error },
  "& .receiver": {
    fontSize: 14,
    fontWeight: 700,
    color: brand.text,
    marginTop: 6,
    lineHeight: 1.2,
  },
});

// ── Misc ────────────────────────────────────────────────────
export const Pad = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}));

export const PadTop = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.25, 2, 0),
}));

export const EmptyRow = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 2),
  fontSize: 14,
  color: brand.textSec,
}));

export const NoResultsMessage = styled(({ ...p }) => (
  <Typography {...p}>No results available.</Typography>
))(({ theme }) => ({
  margin: theme.spacing(3, 2),
  color: brand.textSec,
  textAlign: "center",
}));
